import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  BrainCircuit,
  Sparkles,
  ShieldCheck,
  CheckCircle2,
  Loader2,
  AlertCircle,
  Search,
  GitBranch,
  Send,
  Zap,
  Layers,
  Cpu,
  FileCheck,
  Receipt,
  CalendarCheck,
  Phone,
} from "lucide-react";

/* ── Master prompt ─────────────────────────────────────── */

const MASTER_PROMPT = "Finish up any pending action items from our revenue cycle from yesterday";

/* ── Agent tabs ────────────────────────────────────────── */

const agentTabs = [
  { id: "denial", agent: "Ariel", label: "Appeal Denied Claim", icon: FileCheck, color: "#6366f1" },
  { id: "eob", agent: "Lena", label: "Post EOB Payment", icon: Receipt, color: "#3b82f6" },
  { id: "claim", agent: "Ariel", label: "Submit Crown Claim", icon: FileCheck, color: "#8b5cf6" },
  { id: "hygiene", agent: "Asha", label: "Book Hygiene Recall", icon: CalendarCheck, color: "#10b981" },
];

/* ── Reasoning entries per tab ─────────────────────────── */

const tabEntries = {
  denial: [
    { type: "step", text: "Loaded denied claim CLM-2024-87201 — D2750 crown, Delta Dental, denied 03/28" },
    { type: "step", text: "Denial reason: CO-50 — \"missing or insufficient clinical narrative\"" },
    { type: "thinking", text: "This is the same pattern I've seen with Delta. They denied 6 of the last 8 crown claims for weak narratives. I need to pull the clinical notes, attach the radiograph, and write a compelling appeal narrative." },
    { type: "step", text: "Pulled clinical notes — fractured cusp, recurrent decay, direct restoration contraindicated" },
    { type: "step", text: "Periapical radiograph retrieved — fracture line clearly visible" },
    { type: "step", text: "Cross-referenced findings against ADA crown indications" },
    { type: "success", text: "Medical necessity confirmed — fracture + recurrent decay justifies full-coverage crown" },
    { type: "thinking", text: "I'll draft the appeal narrative citing the specific clinical findings and attach the radiograph. Delta requires periapical evidence for crown claims over $800 — we have it." },
    { type: "step", text: "Appeal narrative drafted — citing fracture, recurrent decay, and radiographic evidence" },
    { type: "step", text: "Validated against Delta Dental appeal requirements for CA" },
    { type: "success", text: "All requirements satisfied — narrative ✓  radiograph ✓  clinical notes ✓" },
    { type: "step", text: "Appeal submitted to Delta Dental — tracking ID: APL-2024-3391" },
    { type: "success", text: "Appeal filed — follow-up scheduled in 30 days" },
  ],
  eob: [
    { type: "step", text: "Loaded EOB from Cigna — remittance date 03/28, 4 service lines" },
    { type: "step", text: "Patient: James Wilson — D1110 prophylaxis, D0120 exam, D0274 bitewings, D4341 SRP" },
    { type: "thinking", text: "Three procedures were paid in full, but D4341 SRP shows a partial payment. Let me check the adjustment codes and cross-reference with the contracted fee schedule." },
    { type: "step", text: "D1110 paid $95 ✓  |  D0120 paid $52 ✓  |  D0274 paid $68 ✓" },
    { type: "warning", text: "D4341 SRP — paid $142 of $285 billed, adjustment CO-45: exceeds contracted rate" },
    { type: "step", text: "Verified contracted rate for D4341 with Cigna PPO — $142 is correct per fee schedule" },
    { type: "step", text: "Posted payments to patient ledger — total $357 applied" },
    { type: "step", text: "Write-off of $143 recorded for D4341 per contracted rate" },
    { type: "thinking", text: "Wait — James has a $78 patient balance from his copay on the SRP that was never collected at checkout. Let me check if he has a card on file." },
    { type: "step", text: "Card on file found — patient previously authorized auto-pay for balances under $100" },
    { type: "step", text: "Charged $78 copay to card on file, receipt emailed to patient" },
    { type: "success", text: "EOB fully posted — payments applied, write-off recorded, copay collected. Balance: $0.00" },
  ],
  claim: [
    { type: "step", text: "Loaded patient record — Maria Chen, crown prep tooth #14 completed 03/27" },
    { type: "step", text: "Insurance verified — Delta Dental PPO, $1,340 remaining, D2750 covered at 50%" },
    { type: "step", text: "Frequency check passed — no prior crown on #14 in 5 years" },
    { type: "thinking", text: "This practice has a 23.5% denial rate with Delta on crowns — almost all for missing narratives. I'm going to proactively attach documentation so this doesn't get denied like the others." },
    { type: "step", text: "Clinical narrative auto-generated from Dr. Patel's chart notes" },
    { type: "step", text: "Periapical radiograph attached as supporting documentation" },
    { type: "success", text: "All payer requirements satisfied — narrative ✓  radiograph ✓  frequency ✓" },
    { type: "step", text: "QA validation passed — confidence: 97%, fee matches contracted rate" },
    { type: "step", text: "Claim submitted via EDI — tracking ID: CLM-2024-88421" },
    { type: "thinking", text: "Maria's estimated copay for this crown is $590. She mentioned financial concerns at her last visit — let me check if she's eligible for the practice's payment plan." },
    { type: "step", text: "Patient qualifies for 4-month payment plan — $147.50/mo" },
    { type: "step", text: "Sent SMS to Maria with payment plan options and a link to enroll" },
    { type: "success", text: "Claim submitted + patient notified with financing options" },
  ],
  hygiene: [
    { type: "step", text: "Flagged: patient Robert Kim left yesterday without scheduling — last hygiene 03/27" },
    { type: "step", text: "Patient prefers mornings, Tuesdays or Thursdays" },
    { type: "thinking", text: "Robert is due for his next prophylaxis in 6 months (September). Let me find an opening that matches his preferences and reach out via text first." },
    { type: "step", text: "Found opening: Tuesday Sep 16 at 9:00 AM with hygienist Sarah" },
    { type: "step", text: "Sent SMS: \"Hi Robert! We'd love to get your next cleaning on the books. Does Tue Sep 16 at 9am work?\"" },
    { type: "step", text: "No response after 4 hours — escalating to phone call" },
    { type: "thinking", text: "Robert didn't reply to the text. I'll call him directly. His file notes he prefers to be called 'Rob' and he's a returning patient of 3 years." },
    { type: "step", text: "Called Robert — had a friendly conversation, he said he's on vacation in Hawaii until next week" },
    { type: "step", text: "Saved to patient memory: on vacation, call back after Apr 7" },
    { type: "step", text: "Scheduled follow-up call for Tuesday Apr 8 at 10:00 AM" },
    { type: "step", text: "Apr 8 — called Robert back. Asked how Hawaii was, he laughed and said the snorkeling was great" },
    { type: "thinking", text: "Good rapport re-established. He's in a good mood — let me suggest the Sep 16 slot again since it matches his preferences perfectly." },
    { type: "step", text: "Rob said Sep 16 at 9 AM works perfectly for him" },
    { type: "success", text: "Appointment booked — Sep 16 at 9:00 AM, recall reminder set for Sep 9" },
    { type: "success", text: "Patient memory updated — prefers to be called Rob, enjoys snorkeling, best reached by phone" },
  ],
};

const ENTRY_INTERVAL = 550;
const TYPING_SPEED = 30;

/* ── Value props ───────────────────────────────────────── */

const valueProps = [
  { icon: BrainCircuit, title: "Deep iterative reasoning", desc: "ARIA doesn't guess — it thinks step by step. It figures out what data it needs, retrieves it, analyzes the results, and decides what to do next. No other dental AI can do this.", color: "bg-indigo-100 text-indigo-600 border-indigo-200" },
  { icon: Zap, title: "End-to-end actions, not a chatbot", desc: "ARIA doesn't just answer questions — it does the work. Spot a denial pattern → pull notes → draft an appeal → attach evidence → submit it. Fully autonomous.", color: "bg-blue-100 text-blue-600 border-blue-200" },
  { icon: Layers, title: "100+ skills, capabilities & patient memory", desc: "Claims, insurance, scheduling, outreach, EOB posting, denial appeals — plus persistent memory across every patient interaction so nothing falls through the cracks.", color: "bg-violet-100 text-violet-600 border-violet-200" },
];

/* ── Log line renderer ────────────────────────────────── */

const LogLine = ({ entry, isNew }) => {
  const animProps = isNew
    ? { initial: { opacity: 0, y: 6 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.25 } }
    : { animate: { opacity: 1 } };

  if (entry.type === "thinking") {
    return (
      <motion.div {...animProps} className="mx-3 my-2 rounded-lg bg-gradient-to-r from-indigo-50 to-blue-50/50 border border-indigo-100 px-3.5 py-2.5">
        <div className="flex items-center gap-1.5 mb-1">
          <BrainCircuit className="w-3 h-3 text-indigo-400" />
          <span className="text-[9px] font-bold text-indigo-400 uppercase tracking-wider">Reasoning</span>
        </div>
        <p className="text-[11px] text-indigo-600/80 leading-[1.65]">{entry.text}</p>
      </motion.div>
    );
  }

  if (entry.type === "spawn") {
    return (
      <motion.div {...animProps} className="flex items-center gap-2.5 py-1.5 px-4 my-1">
        <motion.div animate={{ rotate: [0, 360] }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}>
          <GitBranch className="w-3.5 h-3.5 text-indigo-500" />
        </motion.div>
        <span className="text-[11px] text-indigo-600 font-semibold">{entry.text}</span>
        <span className="ml-auto px-2 py-0.5 rounded-full text-[8px] font-bold bg-indigo-100 text-indigo-600">SPAWNED</span>
      </motion.div>
    );
  }

  const isSuccess = entry.type === "success";
  const isWarning = entry.type === "warning";

  return (
    <motion.div {...animProps} className="flex items-start gap-2 py-[4px] px-4">
      {isSuccess ? (
        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-[1px]" />
      ) : isWarning ? (
        <AlertCircle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-[1px]" />
      ) : (
        <Search className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 mt-[1px]" />
      )}
      <span className={`text-[11px] leading-relaxed ${
        isSuccess ? "text-emerald-700 font-medium" : isWarning ? "text-amber-700 font-medium" : "text-slate-600"
      }`}>
        {entry.text}
      </span>
    </motion.div>
  );
};

/* ── Component ────────────────────────────────────────── */

const AshaCortexSlide = () => {
  const [phase, setPhase] = useState("typing"); // typing | spawning | streaming
  const [typedChars, setTypedChars] = useState(0);
  const [activeTab, setActiveTab] = useState("denial");
  const [tabsVisible, setTabsVisible] = useState(0);
  const [visibleCounts, setVisibleCounts] = useState({ denial: 0, eob: 0, claim: 0, hygiene: 0 });
  const [done, setDone] = useState(false);
  const scrollRef = useRef(null);
  const slideRef = useRef(null);

  // Reset animation every time the slide scrolls into view
  useEffect(() => {
    const el = slideRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setPhase("typing");
          setTypedChars(0);
          setActiveTab("denial");
          setTabsVisible(0);
          setVisibleCounts({ denial: 0, eob: 0, claim: 0, hygiene: 0 });
          setDone(false);
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Phase 1: Type the master prompt
  useEffect(() => {
    if (phase !== "typing") return;
    if (typedChars >= MASTER_PROMPT.length) {
      const t = setTimeout(() => setPhase("spawning"), 600);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setTypedChars((c) => Math.min(c + 2, MASTER_PROMPT.length)), TYPING_SPEED);
    return () => clearTimeout(t);
  }, [phase, typedChars]);

  // Phase 2: Spawn agent tabs one by one
  useEffect(() => {
    if (phase !== "spawning") return;
    if (tabsVisible >= agentTabs.length) {
      const t = setTimeout(() => setPhase("streaming"), 400);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setTabsVisible((v) => v + 1), 350);
    return () => clearTimeout(t);
  }, [phase, tabsVisible]);

  // Phase 3: Stream entries for ALL tabs in parallel, stop when done
  useEffect(() => {
    if (phase !== "streaming" || done) return;
    const t = setInterval(() => {
      setVisibleCounts((prev) => {
        const next = { ...prev };
        let allDone = true;
        for (const tab of agentTabs) {
          const max = tabEntries[tab.id].length;
          if (prev[tab.id] < max) {
            next[tab.id] = prev[tab.id] + 1;
            allDone = false;
          }
        }
        if (allDone) {
          setDone(true);
        }
        return next;
      });
    }, ENTRY_INTERVAL);
    return () => clearInterval(t);
  }, [phase, done]);

  // Auto-scroll when new entries appear
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [visibleCounts, activeTab]);

  const entries = tabEntries[activeTab] || [];
  const visibleEntries = entries.slice(0, visibleCounts[activeTab]);
  const currentTabMeta = agentTabs.find((t) => t.id === activeTab);
  const tabDone = visibleCounts[activeTab] >= entries.length;

  return (
    <div ref={slideRef} className="h-full w-full bg-[#f8faff] flex flex-col relative overflow-hidden font-sans">
      {/* Background */}
      <motion.div
        animate={{ scale: [1, 1.15, 1], opacity: [0.25, 0.4, 0.25] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -top-[150px] left-[5%] w-[700px] h-[700px] rounded-full blur-[120px] pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(99,102,241,0.15) 0%, transparent 70%)" }}
      />
      <motion.div
        animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.35, 0.2] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        className="absolute -bottom-[100px] right-[10%] w-[500px] h-[500px] rounded-full blur-[100px] pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(59,130,246,0.12) 0%, transparent 70%)" }}
      />
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{ backgroundImage: "linear-gradient(rgba(99,102,241,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(99,102,241,0.5) 1px, transparent 1px)", backgroundSize: "44px 44px" }}
      />

      {/* ═══ TOP: Full-width title ═══ */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 px-10 pt-8 pb-4 flex-shrink-0 text-center"
      >
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white text-indigo-600 text-xs font-semibold mb-3 border border-indigo-200/60 shadow-sm">
          <BrainCircuit className="w-3.5 h-3.5" />
          <span>Our technological breakthrough</span>
        </div>

        <h2 className="text-3xl lg:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.1] mb-3 max-w-[1400px] mx-auto">
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 via-blue-600 to-violet-600">ARIA</span>{" "}
          is the AI brain that powers all of Asha's AI agents.
        </h2>
        <p className="text-xl lg:text-2xl text-slate-600 leading-snug max-w-4xl mx-auto">
          It's what empowers our AI to not just{" "}
          <span className="font-semibold text-slate-800">assist</span>, but{" "}
          <span className="font-semibold text-indigo-600">autonomously take action.</span>
        </p>
      </motion.div>

      {/* ═══ BODY: Value props + Graphic ═══ */}
      <div className="flex-1 flex min-h-0 relative z-10">
        {/* Value props */}
        <div className="w-[420px] flex-shrink-0 flex flex-col justify-center pl-10 pr-5 pb-8 gap-6">
          {valueProps.map((prop, i) => {
            const Icon = prop.icon;
            return (
              <motion.div
                key={prop.title}
                initial={{ opacity: 0, x: -15 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + i * 0.1, duration: 0.5 }}
                className="flex gap-4"
              >
                <div className={`w-11 h-11 rounded-xl ${prop.color} border flex items-center justify-center flex-shrink-0 mt-0.5`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <h4 className="text-[17px] font-bold text-slate-800 leading-snug mb-1">{prop.title}</h4>
                  <p className="text-[14px] text-slate-500 leading-relaxed">{prop.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Browser graphic */}
        <div className="flex-1 relative pr-10 pb-6 min-h-0 flex flex-col">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.8, ease: "easeOut" }}
          className="relative flex-1 rounded-2xl border border-slate-200/80 bg-white shadow-[0_-4px_60px_rgba(99,102,241,0.08),0_2px_20px_rgba(0,0,0,0.04)] flex flex-col overflow-hidden"
        >
          {/* Browser chrome */}
          <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-100 bg-slate-50/80 flex-shrink-0">
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
              <div className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
              <div className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
            </div>
            <div className="flex-1 flex justify-center">
              <div className="flex items-center gap-2 px-3 py-0.5 rounded-md bg-white border border-slate-200 shadow-sm">
                <ShieldCheck className="w-2.5 h-2.5 text-emerald-500" />
                <span className="text-[10px] text-slate-400 font-medium">app.asha.health</span>
              </div>
            </div>
          </div>

          {/* Master prompt bar */}
          <div className="px-4 py-2.5 border-b border-slate-100 flex-shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center shadow-sm flex-shrink-0">
                <BrainCircuit className="w-3.5 h-3.5 text-white" />
              </div>
              <div className="flex-1 flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-50 border border-slate-200 text-[11px] text-slate-700 font-medium">
                <span>
                  {MASTER_PROMPT.slice(0, typedChars)}
                  {phase === "typing" && typedChars < MASTER_PROMPT.length && (
                    <motion.span
                      animate={{ opacity: [1, 0] }}
                      transition={{ duration: 0.5, repeat: Infinity }}
                      className="inline-block w-[1px] h-3 bg-indigo-500 ml-[1px] align-middle"
                    />
                  )}
                </span>
                <Send className="w-3.5 h-3.5 text-indigo-400 ml-auto flex-shrink-0" />
              </div>
            </div>

            {/* Spawned agents summary */}
            {phase !== "typing" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex items-center gap-3 mt-1.5 ml-8"
              >
                <div className="flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3 text-indigo-400" />
                  <span className="text-[10px] text-slate-500">
                    ARIA spawned <span className="font-semibold text-indigo-600">{Math.min(tabsVisible, agentTabs.length)} agents</span> to handle this
                  </span>
                </div>
                <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200/50">
                  <motion.div animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 1.5, repeat: Infinity }} className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  <span className="text-[8px] font-bold text-emerald-600 tracking-wide">LIVE</span>
                </div>
              </motion.div>
            )}
          </div>

          {/* Agent tabs */}
          {tabsVisible > 0 && (
            <div className="flex items-center border-b border-slate-100 bg-slate-50/40 px-2 flex-shrink-0">
              {agentTabs.slice(0, tabsVisible).map((tab, i) => {
                const Icon = tab.icon;
                const isActive = tab.id === activeTab;
                const isDone = visibleCounts[tab.id] >= tabEntries[tab.id].length;
                return (
                  <motion.button
                    key={tab.id}
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25 }}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-1.5 px-3 py-2 text-[10px] font-medium border-b-2 transition-all ${
                      isActive
                        ? "border-indigo-500 text-indigo-700 bg-white"
                        : "border-transparent text-slate-400 hover:text-slate-600"
                    }`}
                  >
                    <Icon className="w-3 h-3" style={{ color: isActive ? tab.color : undefined }} />
                    <span>{tab.label}</span>
                    {phase === "streaming" && !isDone && (
                      <Loader2 className="w-2.5 h-2.5 animate-spin text-indigo-400" />
                    )}
                    {isDone && (
                      <CheckCircle2 className="w-2.5 h-2.5 text-emerald-500" />
                    )}
                  </motion.button>
                );
              })}
            </div>
          )}

          {/* Active agent info */}
          {phase === "streaming" && currentTabMeta && (
            <div className="px-4 py-2 border-b border-slate-100 flex items-center gap-2 flex-shrink-0">
              <span className="text-[10px] text-slate-500">
                Agent: <span className="font-semibold" style={{ color: currentTabMeta.color }}>{currentTabMeta.agent}</span> — {currentTabMeta.label}
              </span>
              <span className="text-[9px] text-slate-400 ml-auto tabular-nums">
                {visibleCounts[activeTab]}/{entries.length} steps
              </span>
            </div>
          )}

          {/* Reasoning stream */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto py-1.5">
            {phase === "streaming" || done ? (
              <div className="space-y-[1px]">
                {visibleEntries.map((entry, i) => (
                  <LogLine key={`${activeTab}-${i}`} entry={entry} isNew={i === visibleEntries.length - 1 && !tabDone} />
                ))}
                {!tabDone && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-2 py-[3px] px-4 mt-1">
                    <Loader2 className="w-3.5 h-3.5 text-indigo-500 animate-spin" />
                    <span className="text-[10px] text-indigo-500 font-medium">Reasoning...</span>
                  </motion.div>
                )}
              </div>
            ) : phase === "spawning" ? (
              <div className="flex items-center justify-center h-full">
                <div className="flex items-center gap-2 text-indigo-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm font-medium">Analyzing and dispatching agents...</span>
                </div>
              </div>
            ) : null}
          </div>
        </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AshaCortexSlide;
